import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PatientSectionHeader from '../common/PatientSectionHeader';
import Button from '../../common/Button';
import PrescriptionService from '../../../services/prescriptionService';
import PatientService from '../../../services/patientService';
import OrganizationService from '../../../services/organizationService';
import ProfileService from '../../../services/profileService';
import { formatDateEs, printPrescription, downloadPrescriptionPDF } from '../../../utils';
import './Prescriptions.css';

const Prescriptions = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [patient, setPatient] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [expandedMedications, setExpandedMedications] = useState({});
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  // Rendering the PDF takes a moment, so the row's button reports progress.
  const [downloadingPrescriptionId, setDownloadingPrescriptionId] = useState(null);
  // Filter chips above the list. Maps directly to the visible badges on
  // each card so users see the same vocabulary in both places.
  const [statusFilter, setStatusFilter] = useState('all');

  const statusCounts = useMemo(() => {
    const counts = {
      all: prescriptions.length,
      draft: 0,
      issued: 0,
      cancelled: 0
    };
    for (const prescription of prescriptions) {
      if (prescription.status === 'draft') {
        counts.draft += 1;
      } else if (prescription.status === 'active' || prescription.status === 'archived') {
        counts.issued += 1;
      } else if (prescription.status === 'cancelled') {
        counts.cancelled += 1;
      }
    }
    return counts;
  }, [prescriptions]);

  const filteredPrescriptions = useMemo(() => {
    if (statusFilter === 'all') return prescriptions;
    if (statusFilter === 'draft') {
      return prescriptions.filter((p) => p.status === 'draft');
    }
    if (statusFilter === 'issued') {
      return prescriptions.filter((p) => p.status === 'active' || p.status === 'archived');
    }
    if (statusFilter === 'cancelled') {
      return prescriptions.filter((p) => p.status === 'cancelled');
    }
    return prescriptions;
  }, [prescriptions, statusFilter]);

  // React Router reuses this component instance when navigating between
  // different patients, so reset the filter to avoid showing "no results"
  // for a freshly-loaded patient whose counts wouldn't match the previous
  // patient's filter selection.
  useEffect(() => {
    setStatusFilter('all');
  }, [patientId]);

  useEffect(() => {
    if (hasLoadedOnce) return;
    
    const loadData = async () => {
      const token = auth.user?.access_token;
      const userId = auth.user?.profile?.sub;
      
      if (!token) {
        setIsLoading(false);
        setHasLoadedOnce(true);
        return;
      }
      
      try {
        setIsLoading(true);
        
        // Load all data in parallel
        const [patientData, prescriptionsResponse, organizationData] = await Promise.all([
          PatientService.getPatientById(patientId, token),
          PrescriptionService.getPrescriptionsByPatient(patientId, token),
          OrganizationService.getCurrentUserOrganization(userId, token)
        ]);

        setPatient(patientData);
        setPrescriptions(prescriptionsResponse.data || []);
        setOrganization(organizationData);
        setIsLoading(false);
        setHasLoadedOnce(true);
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Error al cargar la información');
        setIsLoading(false);
        setHasLoadedOnce(true);
      }
    };
    
    loadData();
  }, [hasLoadedOnce, patientId, auth.user?.access_token, auth.user?.profile?.sub]);

  const handleNewPrescription = () => {
    navigate(`/patients/${patientId}/prescriptions/new`);
  };

  const handleViewPrescription = (prescriptionId) => {
    navigate(`/patients/${patientId}/prescriptions/${prescriptionId}`);
  };

  const handleEditPrescription = (prescriptionId) => {
    navigate(`/patients/${patientId}/prescriptions/${prescriptionId}/edit`);
  };

  const handleCancelPrescription = async (prescription) => {
    const reason = prompt('Por favor, ingrese la razón de la cancelación:');
    
    if (!reason || reason.trim().length === 0) {
      return; // User cancelled or provided empty reason
    }

    if (!window.confirm(`¿Está seguro que desea cancelar esta prescripción?\n\nRazón: ${reason}`)) {
      return;
    }

    try {
      const token = auth.user?.access_token;
      await PrescriptionService.cancelPrescription(prescription.id, reason, token);
      
      // Update local state
      setPrescriptions(prevPrescriptions => 
        prevPrescriptions.map(p => 
          p.id === prescription.id ? { ...p, status: 'cancelled', cancelledReason: reason } : p
        )
      );
    } catch (error) {
      console.error('Error cancelling prescription:', error);
      alert('Error al cancelar la prescripción: ' + (error.message || 'Error desconocido'));
    }
  };

  const toggleMedicationsExpanded = (prescriptionId) => {
    setExpandedMedications(prev => ({
      ...prev,
      [prescriptionId]: !prev[prescriptionId]
    }));
  };

  /**
   * Seals the prescription if needed and resolves the doctor, so printing and
   * downloading go through exactly the same gate. Returns null when the user
   * backs out or the sealing fails, meaning the caller must not emit anything.
   */
  const preparePrescriptionForOutput = async (prescription, actionLabel) => {
    let preparedPrescription = prescription;

    // If prescription is draft (not yet printed/sealed), confirm with user and seal it first
    if (prescription.status === 'draft' || !prescription.folioNumber) {
      const confirmed = window.confirm(
        `¿Está seguro que desea ${actionLabel} esta prescripción?\n\n` +
        `Al ${actionLabel}la se sellará con un número de folio y no podrá editarse después.`
      );

      if (!confirmed) {
        return null;
      }

      // Seal the prescription first so the folio appears in the document
      try {
        const token = auth.user?.access_token;
        const result = await PrescriptionService.markAsPrinted(prescription.id, token);
        preparedPrescription = result.data; // Use the sealed prescription with folio

        // Update local state with the sealed prescription data
        setPrescriptions(prevPrescriptions =>
          prevPrescriptions.map(p =>
            p.id === prescription.id ? result.data : p
          )
        );
      } catch (error) {
        console.error('Error sealing prescription:', error);
        alert('Error al sellar la prescripción: ' + (error.message || 'Error desconocido'));
        return null;
      }
    }

    // Fetch doctor information if available
    let doctor = null;
    if (preparedPrescription.doctorId) {
      try {
        const token = auth.user?.access_token;
        doctor = await ProfileService.getUserProfile(preparedPrescription.doctorId, token);
      } catch (error) {
        console.error('Error loading doctor info:', error);
      }
    }

    return { prescription: preparedPrescription, doctor };
  };

  const handlePrintPrescription = async (prescription) => {
    try {
      const prepared = await preparePrescriptionForOutput(prescription, 'imprimir');
      if (!prepared) return;

      printPrescription(prepared.prescription, patient, organization, prepared.doctor, organization?.logoViewUrl);
    } catch (error) {
      console.error('Error printing prescription:', error);
      alert('Error al imprimir la prescripción');
    }
  };

  const handleDownloadPrescription = async (prescription) => {
    try {
      const prepared = await preparePrescriptionForOutput(prescription, 'descargar');
      if (!prepared) return;

      setDownloadingPrescriptionId(prescription.id);
      await downloadPrescriptionPDF(prepared.prescription, patient, organization, prepared.doctor, organization?.logoViewUrl);
    } catch (error) {
      console.error('Error downloading prescription:', error);
      alert('Error al descargar la prescripción');
    } finally {
      setDownloadingPrescriptionId(null);
    }
  };

  if (error && !patient) {
    return (
      <div className="prescriptions-container">
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="prescriptions-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
        sectionTitle="Prescripciones Médicas"
        actionButton={
          <Button
            variant="primary"
            icon="fas fa-plus"
            onClick={handleNewPrescription}
          >
            Nueva Prescripción
          </Button>
        }
      />

      {isLoading ? (
        <div className="prescriptions-loading">
          <div className="loading-spinner"></div>
        </div>
      ) : error ? (
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      ) : prescriptions.length === 0 ? (
        <div className="prescriptions-empty">
          <div className="empty-content">
            <i className="fas fa-prescription-bottle-alt"></i>
            <h3>No hay prescripciones</h3>
            <p>Este paciente aún no tiene prescripciones médicas registradas.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="prescriptions-filter-bar" role="tablist" aria-label="Filtrar prescripciones por estado">
            <Button
              type="button"
              size="small"
              variant={statusFilter === 'all' ? 'primary' : 'outline'}
              onClick={() => setStatusFilter('all')}
              aria-pressed={statusFilter === 'all'}
            >
              Todas <span className="filter-count">{statusCounts.all}</span>
            </Button>
            <Button
              type="button"
              size="small"
              variant={statusFilter === 'draft' ? 'primary' : 'outline'}
              onClick={() => setStatusFilter('draft')}
              aria-pressed={statusFilter === 'draft'}
            >
              Borrador <span className="filter-count">{statusCounts.draft}</span>
            </Button>
            <Button
              type="button"
              size="small"
              variant={statusFilter === 'issued' ? 'primary' : 'outline'}
              onClick={() => setStatusFilter('issued')}
              aria-pressed={statusFilter === 'issued'}
            >
              Emitidas <span className="filter-count">{statusCounts.issued}</span>
            </Button>
            <Button
              type="button"
              size="small"
              variant={statusFilter === 'cancelled' ? 'primary' : 'outline'}
              onClick={() => setStatusFilter('cancelled')}
              aria-pressed={statusFilter === 'cancelled'}
            >
              Canceladas <span className="filter-count">{statusCounts.cancelled}</span>
            </Button>
          </div>

          {filteredPrescriptions.length === 0 ? (
            <div className="prescriptions-empty">
              <div className="empty-content">
                <i className="fas fa-filter"></i>
                <h3>Sin resultados</h3>
                <p>No hay prescripciones con este estado para este paciente.</p>
              </div>
            </div>
          ) : (
        <div className="prescriptions-list">
          {filteredPrescriptions.map((prescription) => (
            <div key={prescription.id} className="prescription-card">
              <div className="prescription-header">
                <div className="prescription-info">
                  <h3>{formatDateEs(prescription.date)}
                    {prescription.folioNumber && (
                      <span className="prescription-folio"> - Folio: {prescription.folioNumber}</span>
                    )}
                    {prescription.status === 'draft' && (
                      <span className="prescription-badge draft-badge">
                        <i className="fas fa-file-alt"></i> Borrador
                      </span>
                    )}
                    {prescription.hasBeenPrinted && prescription.status !== 'draft' && (
                      <span className="prescription-badge printed-badge">
                        <i className="fas fa-file-signature"></i> Emitida
                      </span>
                    )}
                    {prescription.status === 'cancelled' && (
                      <span className="prescription-badge cancelled-badge">
                        <i className="fas fa-ban"></i> Cancelada
                      </span>
                    )}
                  </h3>
                  <p className="prescription-doctor">Dr. {prescription.doctorName}</p>
                </div>
                <div className="prescription-actions">
                  <Button
                    variant="outline"
                    size="small"
                    icon="fas fa-print"
                    onClick={() => handlePrintPrescription(prescription)}
                    disabled={prescription.status === 'cancelled'}
                    title={prescription.status === 'cancelled' ? 'No se puede imprimir una prescripción cancelada' : ''}
                  >
                    <span>Imprimir</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="small"
                    icon={downloadingPrescriptionId === prescription.id ? 'fas fa-spinner fa-spin' : 'fas fa-download'}
                    onClick={() => handleDownloadPrescription(prescription)}
                    disabled={prescription.status === 'cancelled' || downloadingPrescriptionId === prescription.id}
                    title={prescription.status === 'cancelled' ? 'No se puede descargar una prescripción cancelada' : ''}
                  >
                    <span>{downloadingPrescriptionId === prescription.id ? 'Generando...' : 'Descargar'}</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="small"
                    icon="fas fa-eye"
                    onClick={() => handleViewPrescription(prescription.id)}
                  >
                    <span>Ver</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="small"
                    icon="fas fa-edit"
                    onClick={() => handleEditPrescription(prescription.id)}
                    disabled={prescription.hasBeenPrinted || prescription.status === 'cancelled'}
                    title={prescription.hasBeenPrinted ? 'Esta prescripción ya fue emitida y no puede editarse en el sistema' : prescription.status === 'cancelled' ? 'No se puede editar una prescripción cancelada' : ''}
                  >
                    <span>Editar</span>
                  </Button>
                  {prescription.status !== 'cancelled' && (
                    <Button
                      variant="outline"
                      size="small"
                      icon="fas fa-ban"
                      onClick={() => handleCancelPrescription(prescription)}
                    >
                      <span>Cancelar</span>
                    </Button>
                  )}
                </div>

              </div>

              <div className="prescription-content">
                {prescription.generalNotes && (
                  <div className="prescription-general-notes">
                    <p>{prescription.generalNotes}</p>
                  </div>
                )}

                <div className="prescription-medications">
                  <div
                    className="medications-header"
                    onClick={() => toggleMedicationsExpanded(prescription.id)}
                  >
                    <h4>Medicamentos ({prescription.medications?.length || 0})</h4>
                    <i className={`fas fa-chevron-${expandedMedications[prescription.id] ? 'up' : 'down'}`}></i>
                  </div>

                  {expandedMedications[prescription.id] && (
                    <div className="medications-list">
                      {prescription.medications?.map((medication, index) => (
                        <div key={medication.id || index} className="medication-item">
                          <div className="medication-main">
                            <span className="medication-name">
                              {medication.farmaco?.commercialName || medication.farmaco?.components}
                              {medication.farmaco?.commercialName && medication.farmaco?.components && ` (${medication.farmaco?.components})`}
                              {medication.farmaco?.administrationForm && ` - ${medication.farmaco.administrationForm}`}
                            </span>
                            <span className="medication-dose">
                              {medication.farmaco?.dosis}
                            </span>
                          </div>
                          <div className="medication-details">
                            <span className="medication-periodicity">{medication.periodicity} por {medication.duration}</span>
                          </div>
                          {medication.notes && (
                            <div className="medication-notes">
                              <small>{medication.notes}</small>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {prescription.nextCheckupDate && (
                  <div className="prescription-checkup">
                    <div className="checkup-info">
                      <i className="fas fa-calendar-check"></i>
                      <span>Próxima cita de control: {formatDateEs(prescription.nextCheckupDate)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
          )}
        </>
      )}
    </div>
  );
};

export default Prescriptions;
